import sideBarData from "@/assets/json/sidebar-data.json";
import { getActiveCompanyId } from "@/services/_headers.util";
import { isTenancyFrontendEnabled, isTenantHost } from "@/utils/hostname";
import {
  canShowCrmMenuForEmployee,
  isCrmMenuValue,
} from "@/utils/crmAccess";

function normalizeRoles(user) {
  const raw = Array.isArray(user?.roles) ? user.roles : [];
  return raw
    .map((role) => (typeof role === "string" ? role : role?.name))
    .filter(Boolean);
}

function hasPermission(authStore, permission) {
  if (typeof authStore?.can === "function") {
    return !!authStore.can(permission);
  }

  return Array.isArray(authStore?.permissions)
    ? authStore.permissions.includes(permission)
    : false;
}

function buildContext(authStore, accessStore) {
  const roles = normalizeRoles(authStore?.user);
  const activeCompanyId = getActiveCompanyId();
  const isSuperAdmin = roles.includes("SuperAdmin");
  const isAdmin = roles.includes("Admin");
  const isHr = roles.includes("Hr") || roles.includes("HR");
  const isEmployeeRole = roles.includes("Employee");
  const isPureEmployee =
    isEmployeeRole && !isAdmin && !isHr && !isSuperAdmin;

  return {
    roles,
    activeCompanyId,
    hasTenantContext: isTenancyFrontendEnabled() && isTenantHost(),
    isSuperAdmin,
    isAdmin,
    isHr,
    isEmployeeRole,
    isPureEmployee,
    isSuperAdminWithoutCompany: isSuperAdmin && !activeCompanyId,
    planModules: Array.isArray(accessStore?.modules) ? accessStore.modules : [],
    accessStatus: accessStore?.status || "none",
    hasPermission: (permission) => hasPermission(authStore, permission),
  };
}

function getModuleKey(parent, item) {
  if (item?.moduleKey) return item.moduleKey;
  if (parent?.moduleKey) return parent.moduleKey;
  return null;
}

function isLockedKey(key, ctx) {
  if (!key) return false;

  if (!ctx.activeCompanyId && !ctx.hasTenantContext) {
    return !ctx.isSuperAdmin;
  }

  if (ctx.isSuperAdmin) return false;

  if (ctx.accessStatus === "expired" || ctx.accessStatus === "none") {
    return true;
  }

  return !ctx.planModules.includes(key);
}

function shouldShowMenu(menu, ctx) {
  if (menu.menuValue === "Super Admin" && !ctx.isSuperAdmin) {
    return false;
  }

  if (!ctx.isPureEmployee) {
    return true;
  }

  const name = menu.menuValue;

  if (isCrmMenuValue(name)) {
    return canShowCrmMenuForEmployee(name, ctx.hasPermission);
  }

  const denyForEmployee = [
    "Employees",
    "Employee",
    "Employee Details",
    "Promotion",
    "Resignation",
    "Termination",
    "Sales",
    "Accounting",
    "Assets",
    "Help & Supports",
    "User Management",
    "Reports",
    "Settings",
    "Billing",
    "Super Admin",
  ];

  return !denyForEmployee.includes(name);
}

function shouldShowSub(parentMenu, subMenu, ctx) {
  const parent = parentMenu.menuValue;
  const name = subMenu.menuValue;

  if (
    parent === "Applications" &&
    name === "Invoices" &&
    ctx.isPureEmployee
  ) {
    return false;
  }

  if (parent === "Dashboard" && name === "Employee Dashboard") {
    return ctx.isPureEmployee;
  }

  if (parent === "Attendance" && name === "Attendance (Employee)") {
    return ctx.isPureEmployee || ctx.isSuperAdmin;
  }

  if (parent === "Attendance" && name === "Attendance (Admin)") {
    return !ctx.isPureEmployee;
  }

  if (parent === "Attendance" && name === "Timesheets") {
    return !ctx.isPureEmployee;
  }

  if (parent === "Attendance" && name === "My Timesheets") {
    return ctx.isPureEmployee || ctx.isSuperAdmin;
  }

  if (parent === "Attendance" && name === "Shift & Schedule") {
    return !ctx.isPureEmployee;
  }

  if (parent === "Attendance" && name === "Leaves") {
    return true;
  }

  if (
    parent === "Payroll" &&
    (name === "My Payslips" || name === "My P9")
  ) {
    return ctx.isPureEmployee || ctx.isSuperAdmin;
  }

  if (parent === "Payroll" && name === "Payroll Presets") {
    return ctx.isSuperAdmin;
  }

  if (ctx.isPureEmployee) {
    if (parent === "Dashboard") {
      return false;
    }

    if (parent === "Payroll") {
      return false;
    }

    if (parent === "Attendance") {
      return true;
    }

    if (parent === "Performance") {
      return (
        name === "Performance Review List" ||
        name === "Performance Appraisal"
      );
    }

    if (parent === "Training") {
      return name === "Training List";
    }

    return true;
  }

  return true;
}

function shouldShowSubTwo(parentMenu, subMenus, subMenuTwo, ctx) {
  const parent = parentMenu.menuValue;
  const level2 = subMenus.menuValue;
  const name = subMenuTwo.menuValue;

  if (parent === "Attendance" && level2 === "Leaves") {
    if (name === "Leave (Employee)") {
      return ctx.isPureEmployee || ctx.isSuperAdmin;
    }

    if (name === "Leaves (Admin)" || name === "Leave Settings") {
      return ctx.isAdmin || ctx.isHr || ctx.isSuperAdmin;
    }
  }

  return true;
}

function getSuperAdminRootMenus() {
  const main = sideBarData.find((section) => section.tittle === "Main Menu");
  const superAdminMenu = (main?.menu || []).find(
    (menu) => menu.menuValue === "Super Admin",
  );
  const allowedLabels = [
    "Dashboard",
    "Companies",
    "Subscriptions",
    "Packages",
    "Purchase Transaction",
  ];

  return (superAdminMenu?.subMenus || []).filter((subMenu) =>
    allowedLabels.includes(subMenu.menuValue),
  );
}

function toNeedle(...parts) {
  return parts
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

function toPageItem({ id, title, route, icon, pathParts, moduleKey }) {
  return {
    id,
    type: "page",
    title,
    subtitle: pathParts.join(" / "),
    description: moduleKey ? `Module: ${moduleKey}` : "Navigation page",
    route,
    icon: icon || "search",
  };
}

function matchesQuery(query, item) {
  const needle = query.toLowerCase();
  return toNeedle(item.title, item.subtitle, item.description, item.route).includes(
    needle,
  );
}

export function findAccessiblePages(query, authStore, accessStore, limit = 8) {
  const q = String(query || "").trim();
  if (q.length < 2) return [];

  const ctx = buildContext(authStore, accessStore);
  const items = [];

  if (ctx.isSuperAdminWithoutCompany) {
    getSuperAdminRootMenus().forEach((menu) => {
      const item = toPageItem({
        id: `superadmin-${menu.route}`,
        title: menu.menuValue,
        route: menu.route,
        icon: menu.icon || "user-star",
        pathParts: ["Main Menu", "Super Admin"],
      });
      if (matchesQuery(q, item)) {
        items.push(item);
      }
    });

    return items.slice(0, limit);
  }

  sideBarData.forEach((section) => {
    (section.menu || []).forEach((menu) => {
      if (!shouldShowMenu(menu, ctx)) return;

      if (!menu.hasSubRoute && !menu.hasSubRouteTwo) {
        const moduleKey = getModuleKey(null, menu);
        if (!menu.route || isLockedKey(moduleKey, ctx)) return;

        const item = toPageItem({
          id: `page-${menu.route}`,
          title: menu.menuValue,
          route: menu.route,
          icon: menu.icon || "dot",
          pathParts: [section.tittle],
          moduleKey,
        });

        if (matchesQuery(q, item)) {
          items.push(item);
        }
        return;
      }

      if (menu.hasSubRoute && !menu.hasSubRouteTwo) {
        (menu.subMenus || []).forEach((subMenu) => {
          if (!shouldShowSub(menu, subMenu, ctx)) return;

          const moduleKey = getModuleKey(menu, subMenu);
          if (!subMenu.route || isLockedKey(moduleKey, ctx)) return;

          const item = toPageItem({
            id: `page-${subMenu.route}`,
            title: subMenu.menuValue,
            route: subMenu.route,
            icon: menu.icon || "dot",
            pathParts: [section.tittle, menu.menuValue],
            moduleKey,
          });

          if (matchesQuery(q, item)) {
            items.push(item);
          }
        });
        return;
      }

      if (!menu.hasSubRouteTwo) return;

      (menu.subMenus || []).forEach((subMenus) => {
        if (!shouldShowSub(menu, subMenus, ctx)) return;

        if (!subMenus.customSubmenuTwo) {
          const moduleKey = getModuleKey(menu, subMenus);
          if (!subMenus.route || isLockedKey(moduleKey, ctx)) return;

          const item = toPageItem({
            id: `page-${subMenus.route}`,
            title: subMenus.menuValue,
            route: subMenus.route,
            icon: menu.icon || "dot",
            pathParts: [section.tittle, menu.menuValue],
            moduleKey,
          });

          if (matchesQuery(q, item)) {
            items.push(item);
          }
          return;
        }

        (subMenus.subMenusTwo || []).forEach((subMenuTwo) => {
          if (!shouldShowSubTwo(menu, subMenus, subMenuTwo, ctx)) return;

          const moduleKey = getModuleKey(menu, subMenuTwo);
          if (!subMenuTwo.route || isLockedKey(moduleKey, ctx)) return;

          const item = toPageItem({
            id: `page-${subMenuTwo.route}`,
            title: subMenuTwo.menuValue,
            route: subMenuTwo.route,
            icon: menu.icon || "dot",
            pathParts: [section.tittle, menu.menuValue, subMenus.menuValue],
            moduleKey,
          });

          if (matchesQuery(q, item)) {
            items.push(item);
          }
        });
      });
    });
  });

  const seen = new Set();
  return items.filter((item) => {
    if (seen.has(item.route)) return false;
    seen.add(item.route);
    return true;
  }).slice(0, limit);
}

export function mergeSearchSections(pageItems, dataSections) {
  const sections = [];

  if (Array.isArray(pageItems) && pageItems.length) {
    sections.push({
      key: "pages",
      label: "Pages",
      icon: "app-window",
      items: pageItems,
    });
  }

  if (Array.isArray(dataSections) && dataSections.length) {
    sections.push(...dataSections);
  }

  return sections;
}
